import React, { useMemo, useCallback } from 'react';
import SdkAgGrid from '@tls/ui-aggrid';

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}

function toPrimitive(t, r) {
  if ("object" != _typeof(t) || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != _typeof(i)) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}

function toPropertyKey(t) {
  var i = toPrimitive(t, "string");
  return "symbol" == _typeof(i) ? i : i + "";
}

function _defineProperty(e, r, t) {
  return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: !0,
    configurable: !0,
    writable: !0
  }) : e[r] = t, e;
}

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}

function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (e.indexOf(n) >= 0) continue;
    t[n] = r[n];
  }
  return t;
}

function _objectWithoutProperties(e, t) {
  if (null == e) return {};
  var o,
    r,
    i = _objectWithoutPropertiesLoose(e, t);
  if (Object.getOwnPropertySymbols) {
    var n = Object.getOwnPropertySymbols(e);
    for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
  }
  return i;
}

const CACHE_BLOCK_SIZE = 50;
const serverSideColumnOptions = {
  filter: 'agTextColumnFilter'
};

const _excluded = ["columnDefs"];
const ServerSideAgGrid = _ref => {
  let {
      columnDefs
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const columnDefinitions = useMemo(() => columnDefs.map(columnDef => _objectSpread2(_objectSpread2({}, serverSideColumnOptions), columnDef)), [columnDefs]);
  return /*#__PURE__*/React.createElement(SdkAgGrid, _extends({
    columnDefs: columnDefinitions,
    rowModelType: "serverSide",
    cacheBlockSize: CACHE_BLOCK_SIZE
  }, rest));
};

const parseRowCount = value => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsedValue = Number.parseInt(value, 10);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }
  return undefined;
};
const normalizeFetchResult = result => {
  var _ref, _ref2, _result$rowData, _result$rowCount;
  if (Array.isArray(result)) {
    return {
      rowData: result
    };
  }
  if (!result || typeof result !== 'object') {
    return {
      rowData: []
    };
  }
  const rowData = (_ref = (_ref2 = (_result$rowData = result.rowData) !== null && _result$rowData !== void 0 ? _result$rowData : result.data) !== null && _ref2 !== void 0 ? _ref2 : result.rows) !== null && _ref !== void 0 ? _ref : [];
  const rowCount = parseRowCount((_result$rowCount = result.rowCount) !== null && _result$rowCount !== void 0 ? _result$rowCount : result.total);
  return _objectSpread2({
    rowData
  }, rowCount !== undefined ? {
    rowCount
  } : {});
};
const reportSuccess = (params, _ref3) => {
  let {
    rowData,
    rowCount
  } = _ref3;
  const loadSuccessParams = {
    rowData: rowData !== null && rowData !== void 0 ? rowData : []
  };
  const parsedRowCount = parseRowCount(rowCount);
  if (parsedRowCount !== undefined) {
    loadSuccessParams.rowCount = parsedRowCount;
  }
  if (typeof params.success === 'function') {
    params.success(loadSuccessParams);
    return;
  }
  if (typeof params.successCallback === 'function') {
    params.successCallback(loadSuccessParams.rowData, loadSuccessParams.rowCount);
  }
};
const reportFail = params => {
  if (typeof params.fail === 'function') {
    params.fail();
    return;
  }
  if (typeof params.failCallback === 'function') {
    params.failCallback();
  }
};
const setServerSideDatasource = (api, datasource) => {
  if (typeof api.setGridOption === 'function') {
    api.setGridOption('serverSideDatasource', datasource);
    return;
  }
  if (typeof api.setServerSideDatasource === 'function') {
    api.setServerSideDatasource(datasource);
  }
};
const useGridServerSideDataSource = _ref4 => {
  let {
    fetchData,
    initialRows = false
  } = _ref4;
  const createDatasource = useCallback(() => {
    let initialRowsUsed = false;
    return {
      getRows: async params => {
        if (!initialRowsUsed && initialRows) {
          reportSuccess(params, normalizeFetchResult(initialRows));
          initialRowsUsed = true;
          return;
        }
        try {
          const result = await fetchData({
            limit: params.request.endRow - params.request.startRow,
            offset: params.request.startRow,
            sortModel: params.request.sortModel,
            filterModel: params.request.filterModel
          });
          reportSuccess(params, normalizeFetchResult(result));
        } catch (error) {
          reportFail(params);
        }
      }
    };
  }, [fetchData, initialRows]);
  const onGridReady = useCallback(_ref5 => {
    let {
      api
    } = _ref5;
    setServerSideDatasource(api, createDatasource());
  }, [createDatasource]);
  return {
    onGridReady
  };
};

export { CACHE_BLOCK_SIZE, ServerSideAgGrid as default, serverSideColumnOptions, useGridServerSideDataSource };
